import { useEffect, useMemo, useRef } from "react";
import { useGLTF, useAnimations } from "@react-three/drei";
import * as THREE from "three";
import { SkeletonUtils } from "three-stdlib";

const FALLBACK_FPS = 30;

export default function Teapot({
  position = [12.42, 4.58, 0.62],
  rotation = [0, 0, 0],
  scale = 1,
  color = "#F8C52C",
  startFrame = 0,
  ...props
}) {
  const group = useRef();
  const { scene, animations } = useGLTF("/teapot.glb");
  const clonedScene = useMemo(() => SkeletonUtils.clone(scene), [scene]);
  const { actions } = useAnimations(animations, group);
  const desiredColor = useMemo(() => new THREE.Color(color), [color]);

  useEffect(() => {
    const applyColor = (material) => {
      if (!material) return material;
      if (Array.isArray(material)) {
        return material.map((entry) => applyColor(entry));
      }
      const cloned = material.clone();
      if (cloned.color) {
        cloned.color.copy(desiredColor);
      }
      return cloned;
    };

    clonedScene.traverse((child) => {
      if (child.isMesh || child.isSkinnedMesh) {
        child.castShadow = true;
        child.receiveShadow = true;
        child.material = applyColor(child.material);
      }
    });
  }, [clonedScene, desiredColor]);

  useEffect(() => {
    if (!actions) return undefined;

    const getFrameDuration = (clip) => {
      if (!clip?.tracks) return null;
      for (const track of clip.tracks) {
        if (!track?.times || track.times.length < 2) continue;
        const first = track.times[0];
        const last = track.times[track.times.length - 1];
        const total = last - first;
        const steps = track.times.length - 1;
        if (total > 0 && steps > 0) {
          return total / steps;
        }
      }
      return null;
    };

    const actionList = Object.values(actions).filter(Boolean);
    actionList.forEach((action) => {
      const clip = typeof action.getClip === "function" ? action.getClip() : null;
      const frameDuration = clip ? getFrameDuration(clip) : null;
      const rawOffset = frameDuration && Number.isFinite(frameDuration)
        ? startFrame * frameDuration
        : (startFrame / FALLBACK_FPS);
      const duration = clip?.duration ?? 0;
      let timeOffset = rawOffset;
      if (duration > 0) {
        timeOffset = ((rawOffset % duration) + duration) % duration;
      }

      action.reset();
      action.setLoop(THREE.LoopRepeat, Infinity);
      action.time = timeOffset;
      action.clampWhenFinished = false;
      action.paused = false;
      action.play();
    });

    return () => {
      actionList.forEach((action) => {
        action.stop();
      });
    };
  }, [actions, startFrame]);

  return (
    <group ref={group} position={position} rotation={rotation} scale={scale} {...props}>
      <primitive object={clonedScene} />
    </group>
  );
}

useGLTF.preload("/teapot.glb");
